import {
  BrandfolderHexColors,
  CodeEditorLanguages,
  FontIcon,
  FontIcons,
  FontSizes,
  FontWeights,
  StandardCodeEditor,
  StandardHeading,
  useFetch,
  UseFetchOptions
} from '@brandfolder/react';
import { yupResolver } from '@hookform/resolvers/yup';
import classNames from 'classnames';
import { FunctionComponent, HTMLAttributes, useContext, useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-hot-toast';
import styled from 'styled-components';
import { object, SchemaOf, string } from 'yup';

import { BrandguideContext } from '@context/brandguide';
import { ThemeContext } from '@context/theme';
import { ApiDatum } from '@typings/api';
import { Theme, ThemeCustom } from '@typings/theme';

interface SettingsCustomCssValues {
  css?: string;
}

const schema: SchemaOf<SettingsCustomCssValues> = object().shape({
  css: string().defined()
});

interface SettingsCustomCssProps extends HTMLAttributes<HTMLFormElement> {}

const UnstyledSettingsCustomCSS: FunctionComponent<SettingsCustomCssProps> = (props) => {
  const { brandguide } = useContext(BrandguideContext);
  const { theme, setTheme } = useContext(ThemeContext);

  const ref = useRef('');

  const { formState, handleSubmit, setValue } = useForm<SettingsCustomCssValues>({
    defaultValues: {
      css: theme.custom.css
    },
    mode: 'onBlur',
    resolver: yupResolver(schema),
    reValidateMode: 'onBlur',
    shouldFocusError: false
  });

  const { errors } = formState;

  const options: UseFetchOptions = {
    fetchOnMount: false,
    headers: {
      'Content-Type': 'application/json',
      'x-brandguide-key': brandguide?.key || ''
    },
    method: 'PATCH',
    url: `/api/themes/${theme.key}`
  };

  const { error, fetch, loading, response } = useFetch<ApiDatum<Partial<Theme>>>(options);

  useEffect(() => {
    if (loading) {
      ref.current = toast.loading('Saving...');
    }
  }, [loading]);

  useEffect(() => {
    if (response) {
      setTheme({
        ...theme,
        custom: {
          ...theme.custom,
          css: response.data.attributes.custom?.css || ''
        }
      });
      toast.dismiss(ref.current);
      toast.success('Saved!');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [response]);

  useEffect(() => {
    if (error) {
      toast.dismiss(ref.current);
      toast.error('Error saving.');
    }
  }, [error]);

  return (
    <form
      {...props}
      className={classNames({
        'global-settings__wrapper': true,
        [`${props.className}`]: !!props.className
      })}
      noValidate
      onBlur={handleSubmit((values) => {
        const custom: ThemeCustom = {
          css: values.css || ''
        };

        fetch({
          ...options,
          body: {
            data: {
              attributes: {
                custom
              }
            }
          }
        });
      })}
    >
      <div className="custom-css-header-wrapper">
        <FontIcon className="embed" icon={FontIcons.Embed} iconSize={20} />
        <StandardHeading
          className="custom-css-text"
          size={FontSizes.XSmall}
          weight={FontWeights.Medium}
        >
          Customization
        </StandardHeading>
      </div>
      <StandardCodeEditor
        className="custom-css-code-editor"
        defaultValue={theme.custom.css}
        error={errors.css?.message}
        id="settings-custom-css"
        label="CSS"
        language={CodeEditorLanguages.Css}
        onEdit={(code) => {
          setValue('css', code, { shouldDirty: true, shouldTouch: true });
        }}
      />
    </form>
  );
};

const StyledSettingsCustomCss = styled(UnstyledSettingsCustomCSS)`
  .custom-css-header-wrapper {
    align-items: center;
    display: flex;
    margin: 24px 0px 10px 0px;

    .embed {
      margin-right: 15px;
    }

    .custom-css-text {
      text-transform: uppercase;
    }
  }

  .bf-code-editor__editor {
    background-color: ${BrandfolderHexColors.White};
  }
`;

export const SettingsCustomCss: FunctionComponent<SettingsCustomCssProps> = (props) => {
  return <StyledSettingsCustomCss {...props} />;
};
